'use client';

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { NpApplicationFormCompact } from "@/modules/student/components/np-application-form-compact"
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  HeartPulse,
  Clock3,
  GraduationCap,
  Users2,
  MapPin,
  FileBadge,
  Plus,
  Minus,
} from "lucide-react"
import { Animated, StaggerContainer } from '@/components/ui/animated'

export default function NpApplicationPage() {
  const heroHighlights = [
    {
      icon: GraduationCap,
      title: "All major NP programs",
      description: "Walden, Chamberlain, Purdue, Maryville, and 25+ more — we work with them all.",
    },
    {
      icon: Clock3,
      title: "Flexible timing",
      description: "Match your rotation to your class schedule and start window.",
    },
    {
      icon: HeartPulse,
      title: "Vetted preceptors",
      description: "MDs, NPs, and therapists matched to your specialty track.",
    },
  ]

  const valueCards = [
    {
      icon: Sparkles,
      title: "Concierge onboarding",
      description: "A dedicated coordinator walks you through documentation, requirements, and timelines.",
    },
    {
      icon: FileBadge,
      title: "Documents handled once",
      description: "We collect your paperwork up front and keep it ready for future rotations.",
    },
    {
      icon: Users2,
      title: "Personalized matching",
      description: "We pair you with preceptors whose practice fits your learning goals and specialty track.",
    },
    {
      icon: MapPin,
      title: "Local or telehealth",
      description: "Choose in-person rotations near you or virtual placements when your program allows.",
    },
  ]

  const howItWorks = [
    {
      title: "Share your details",
      description: "Tell us your program, track, hours, and timing preferences.",
    },
    {
      title: "Match with a preceptor",
      description: "We introduce vetted preceptors that fit your goals and availability.",
    },
    {
      title: "Start your rotation",
      description: "We coordinate paperwork, scheduling, and check-ins so you can focus on learning.",
    },
  ]

  const faqs = [
    {
      q: "Which universities do you work with?",
      a: "We support students from Walden, Chamberlain, Purdue, Maryville, George Washington, Liberty, Grand Canyon, and 20+ more programs. Don't see yours? Select \"Other\" and we'll reach out.",
    },
    {
      q: "How long does matching take?",
      a: "Most students hear back within a few business days. Timing depends on specialty, location, and your preferred start window.",
    },
    {
      q: "Do you support tele-medicine rotations?",
      a: "Yes — when your university allows it. Let us know on the form and we'll match you with preceptors who offer hybrid or fully-remote options.",
    },
    {
      q: "Can I bring friends from the same program?",
      a: "Absolutely. List their names and contact info on the form and we'll reach out to them too.",
    },
  ]

  return (
    <div className="flex flex-col bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-sky-800 to-indigo-700 text-white">
        <div className="absolute inset-0">
          <div className="absolute -top-24 -right-12 h-80 w-80 rounded-full bg-sky-400/20 blur-3xl" />
          <div className="absolute bottom-0 left-10 h-80 w-80 rounded-full bg-indigo-300/20 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.12),transparent_40%)]" />
        </div>

        <div className="section-inner relative grid items-center gap-12 py-20 lg:grid-cols-[1.05fr,0.95fr]">
          <Animated animation="fade-right" duration={800}>
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-sky-50 ring-1 ring-white/20">
                <GraduationCap className="h-4 w-4" />
                NP Application
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl">
                  Find your next NP rotation
                </h1>
                <p className="max-w-2xl text-lg text-sky-50/90">
                  We help nurse practitioner students from Walden, Chamberlain, Purdue, and 25+ other programs match
                  with vetted preceptors. Tell us about your program and we&apos;ll handle the rest.
                </p>
              </div>

              <StaggerContainer staggerDelay={100} animation="fade-up" className="grid gap-4 sm:grid-cols-2">
                {heroHighlights.map((item) => {
                  const Icon = item.icon
                  return (
                    <div
                      key={item.title}
                      className="flex gap-3 rounded-xl bg-white/5 p-4 ring-1 ring-white/10 backdrop-blur hover:bg-white/10 transition-colors duration-300"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="text-xs text-sky-50/80">{item.description}</p>
                      </div>
                    </div>
                  )
                })}
              </StaggerContainer>

              <div className="flex flex-wrap items-center gap-4">
                <Link href="#form">
                  <Button className="bg-white px-5 py-2.5 text-blue-800 shadow-lg shadow-blue-900/20 hover:bg-blue-50 hover:scale-105 transition-transform">
                    Apply now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <div className="flex items-center gap-3 text-sm text-sky-50/80">
                  <ShieldCheck className="h-4 w-4" />
                  No cost to apply. We&apos;ll reach out within a few business days.
                </div>
              </div>
            </div>
          </Animated>

          {/* Form */}
          <Animated animation="fade-left" delay={200} duration={800}>
            <div
              id="form"
              className="rounded-2xl border border-white/20 bg-white text-slate-900 shadow-2xl shadow-blue-900/30"
            >
              <div className="space-y-2 border-b border-slate-100 px-8 py-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                  <GraduationCap className="h-4 w-4" />
                  NP student application
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Tell us about your rotation needs</h2>
                <p className="text-sm text-slate-600">
                  Share your program details and timeline. We&apos;ll match you with a vetted preceptor.
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                    <GraduationCap className="h-3.5 w-3.5" />
                    25+ universities
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                    <HeartPulse className="h-3.5 w-3.5" />
                    All NP tracks
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Concierge support
                  </span>
                </div>
              </div>
              <div className="px-8 py-6">
                <NpApplicationFormCompact />
              </div>
            </div>
          </Animated>
        </div>
      </section>

      {/* Value Section */}
      <section className="bg-white py-16">
        <div className="section-inner space-y-10">
          <Animated animation="fade-up">
            <div className="flex flex-col gap-3 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">What you get</p>
              <h2 className="text-3xl font-bold text-slate-900">A smoother way to find your NP rotation</h2>
              <p className="mx-auto max-w-3xl text-base text-slate-600">
                From paperwork to ongoing check-ins, we keep the logistics light so you can focus on your clinical learning.
              </p>
            </div>
          </Animated>
          <StaggerContainer staggerDelay={100} animation="fade-up" className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {valueCards.map((item) => {
              const Icon = item.icon
              return (
                <div
                  key={item.title}
                  className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm shadow-blue-900/5 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                </div>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 py-16">
        <div className="section-inner space-y-12">
          <Animated animation="fade-up">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">How it works</p>
                <h2 className="mt-2 text-3xl font-bold text-slate-900">From application to rotation in three steps</h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  We keep you informed at every stage and handle the coordination so you can focus on your studies.
                </p>
              </div>
              <Link href="#form">
                <Button variant="outline" className="border-blue-200 text-blue-800 hover:bg-blue-50 hover:scale-105 transition-transform">
                  Start your application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Animated>

          <StaggerContainer staggerDelay={150} animation="fade-up" className="grid gap-6 md:grid-cols-3">
            {howItWorks.map((step, index) => (
              <div
                key={step.title}
                className="relative rounded-2xl border border-blue-100 bg-white p-6 shadow-sm shadow-blue-900/5 hover:shadow-lg transition-all duration-300"
              >
                <div className="absolute -top-4 flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg shadow-blue-900/20">
                  {index + 1}
                </div>
                <div className="mt-4 space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gray-50 py-16">
        <div className="section-inner max-w-3xl space-y-8">
          <Animated animation="fade-up">
            <div className="space-y-2 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Questions</p>
              <h2 className="text-3xl font-bold text-slate-900">Common questions from NP students</h2>
              <p className="text-sm text-slate-600">
                Still unsure? Reach out after you apply and we&apos;ll tailor the process to your needs.
              </p>
            </div>
          </Animated>
          <StaggerContainer staggerDelay={100} animation="fade-up" className="space-y-3">
            {faqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm shadow-blue-900/5 hover:shadow-md transition-all duration-300"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-left text-sm font-semibold text-slate-900 transition-colors group-open:text-blue-700">
                  {faq.q}
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-500 group-open:border-blue-200 group-open:bg-blue-50 group-open:text-blue-700 transition-all duration-300">
                    <Plus className="h-3 w-3 group-open:hidden" />
                    <Minus className="hidden h-3 w-3 group-open:block" />
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 animate-in fade-in slide-in-from-top-2 duration-300">{faq.a}</p>
              </details>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-700 to-sky-600 py-16 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.15),transparent_40%)]" />
        <div className="section-inner relative flex flex-col items-center gap-4 text-center">
          <Animated animation="fade-up">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-100">Ready to start your rotation?</p>
          </Animated>
          <Animated animation="fade-up" delay={100}>
            <h2 className="text-3xl font-bold leading-tight text-white md:text-4xl">
              Vetted NP preceptors across the country
            </h2>
          </Animated>
          <Animated animation="fade-up" delay={200}>
            <p className="max-w-3xl text-base text-sky-50/90">
              Tell us your preferences and we&apos;ll match you with a vetted preceptor on your timeline.
            </p>
          </Animated>
          <Animated animation="scale" delay={300}>
            <Link href="#form">
              <Button className="bg-white text-blue-800 hover:bg-blue-50 hover:scale-105 transition-transform">
                Start your application
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Animated>
        </div>
      </section>
    </div>
  )
}
